/**
 * LocalIssuer — self-issue BBS+ credentials from normalized claims.
 *
 * Each BBS+ message is a length-prefixed concat of typed chunks. The
 * length-prefix prevents encoding ambiguity; the chunk-prefix label
 * (`audience:v1`, `attribute:v1`, `rep`) identifies the fact kind.
 *
 * Slice 0 / AT-13 + AT-14 (ADR 0005 §"Canonical message encoding (5-chunk,
 * versioned-prefix)" §"Taxonomy field discipline (Q5)" §"Decoder shape
 * change & verifier semantics (Q8)"): the legacy 3-tuple
 * `[label="interest[i]", category, confidence]` and 2-tuple
 * `[label="demo[key]", value]` shapes are retired permanently in favour of
 * the canonical 5-tuples
 *   `[audience:v1, section, taxonomy, id, confidence]`        (interests / purchase_intent)
 *   `[attribute:v1, section, key, value, confidence]`         (demographics / household)
 *
 * Confidence is encoded canonically as IEEE 754 big-endian float32; the
 * encoder rejects NaN, non-finite, and out-of-range `[0, 1]` values for
 * both audience-segment and user-attribute claims.
 *
 * Message layout (no derivation_policy_version as message 0):
 *   0..N-1:  [audience:v1, "interests", taxonomy, id, confidence]
 *   N..M-1:  [audience:v1, "purchase_intent", taxonomy, id, confidence]
 *   M..K-1:  [attribute:v1, "demographics", key, value, confidence]
 *            — emitted only when `demographics_confidence[key]` is finite
 *              and in [0,1]; AT-14 ships gender; AT-15 + AT-19 multiply.
 *   K..end:  [rep, level]                             — legacy preserved (Q8)
 *
 * Spike-gate exception scope: this file's `claimsToMessages`,
 * `decodeDisclosedMessage`, and `rebuildDisclosedMessages` are ADR 0005-
 * named carve-outs from ADR 0001's spike-gate rule. No diff to
 * crypto-primitive bodies.
 *
 * @traces §2.2, F-H4, ADR 0005 Q2 + Q3 + Q5 + Q8, AT-13, AT-14
 */
import { lengthPrefixedConcat, utf8 } from '../crypto/encoding.js';
const BBS_HEADER = utf8('OwnYou-BBS-Header-v2');
// ADR 0005 Q5 format-guard regex. Anchored both ends. Lowercase + dashes
// + version suffix. Rejects e.g. "IAB Audience 1.1", "iab_audience_1.1",
// "iab-audience" (no version), "iab-audience-1.1.1" (three-version dots).
const TAXONOMY_FORMAT_GUARD = /^[a-z0-9]+(-[a-z0-9]+)*-[0-9]+\.[0-9]+$/;
// ADR 0005 Q8: audience id format-guard. Numeric, leading non-zero,
// 1..6 digits.
const AUDIENCE_ID_REGEX = /^[1-9][0-9]{0,5}$/;
const AUDIENCE_SECTIONS = new Set([
    'interests',
    'purchase_intent',
]);
const ATTRIBUTE_SECTIONS = new Set([
    'demographics',
    'household',
]);
const REPUTATION_LEVELS = new Set([
    'R0',
    'R1',
    'R2',
    'R3',
    'R4',
    'R5',
]);
const AUDIENCE_LABEL_V1 = 'audience:v1';
const ATTRIBUTE_LABEL_V1 = 'attribute:v1';
const REP_LABEL = 'rep';
export async function issueCredential(suite, claims, keyPair) {
    const messages = claimsToMessages(claims);
    return suite.bbsSign({
        secretKey: keyPair.secretKey,
        publicKey: keyPair.publicKey,
        messages,
        header: BBS_HEADER,
    });
}
export function bbsHeader() {
    return BBS_HEADER.slice();
}
function encodeConfidence(c) {
    if (!Number.isFinite(c)) {
        throw new TypeError(`encodeConfidence: non-finite value ${c}`);
    }
    if (c < 0 || c > 1) {
        throw new RangeError(`encodeConfidence: confidence ${c} out of range [0, 1]`);
    }
    const buf = new ArrayBuffer(4);
    new DataView(buf).setFloat32(0, c, false);
    return new Uint8Array(buf);
}
function encodeAudienceSegment(section, claim) {
    const taxonomy = claim.taxonomy;
    if (typeof taxonomy !== 'string' || taxonomy.length === 0) {
        throw new TypeError(`claimsToMessages: ${section} claim missing required taxonomy field (claim.category=${JSON.stringify(claim.category)})`);
    }
    if (!TAXONOMY_FORMAT_GUARD.test(taxonomy)) {
        throw new TypeError(`claimsToMessages: taxonomy ${JSON.stringify(taxonomy)} fails format-guard regex ${TAXONOMY_FORMAT_GUARD.source}`);
    }
    return lengthPrefixedConcat([
        utf8(AUDIENCE_LABEL_V1),
        utf8(section),
        utf8(taxonomy),
        utf8(claim.category),
        encodeConfidence(claim.confidence),
    ]);
}
// AT-14 / ADR 0005 Q2 + Q3 + Q8 — user-attribute encoder.
//
// Produces the canonical `attribute:v1` 5-tuple for a single demographic
// or household claim. Confidence is in the signed surface; `encodeConfidence`
// rejects NaN, non-finite, and out-of-range values pre-encode.
function encodeUserAttribute(section, key, value, confidence) {
    if (typeof key !== 'string' || key.length === 0) {
        throw new TypeError(`claimsToMessages: ${section} claim has empty key`);
    }
    if (typeof value !== 'string') {
        throw new TypeError(`claimsToMessages: ${section}.${key} value must be a string`);
    }
    return lengthPrefixedConcat([
        utf8(ATTRIBUTE_LABEL_V1),
        utf8(section),
        utf8(key),
        utf8(value),
        encodeConfidence(confidence),
    ]);
}
// AT-14 / ADR 0005 Q3 — emit attribute:v1 messages for a demographic-shaped
// map (Record<string, string>) joined with the per-key confidence sidecar.
// Keys without a finite confidence in [0,1] are silently dropped — the
// legacy `demo[key]` 2-tuple is retired permanently per ADR 0005 Q8.
//
// `confidenceMap` may be undefined (legacy callers); in that case nothing
// is emitted from the section. Per KAD-0.4, demographics on the wire stays
// `Record<string, string>`; the confidence sidecar provides the
// probability-bearing component without changing the value-shape.
function encodeAttributeSection(section, values, confidenceMap) {
    if (!confidenceMap)
        return [];
    const out = [];
    const sortedKeys = Object.keys(values).sort();
    for (const key of sortedKeys) {
        const confidence = confidenceMap[key];
        if (typeof confidence !== 'number')
            continue;
        out.push(encodeUserAttribute(section, key, values[key], confidence));
    }
    return out;
}
export function claimsToMessages(claims) {
    const messages = [];
    for (const claim of claims.interests) {
        messages.push(encodeAudienceSegment('interests', claim));
    }
    for (const claim of claims.purchase_intent) {
        messages.push(encodeAudienceSegment('purchase_intent', claim));
    }
    // AT-14: demographics emit as `attribute:v1` 5-tuples. The legacy
    // `demo[key]` 2-tuple is permanently retired. Demographic keys without
    // a corresponding `demographics_confidence` entry are dropped — the
    // wire-builder is responsible for plumbing per-key confidence.
    //
    // KAD-0.5: household-section records merge into `claims.demographics`
    // on the wire, so the `claims.household` map is structurally empty
    // under the v3 wire path. We still emit attribute:v1 5-tuples for any
    // residual entries in `claims.household` so non-Slice-0 callers (older
    // fixtures, future direct-household producers) still work.
    for (const msg of encodeAttributeSection('demographics', claims.demographics, claims.demographics_confidence)) {
        messages.push(msg);
    }
    // Residual household path — empty under the Slice 0 wire-builder
    // (per KAD-0.5) but preserved for non-wire-builder callers. No
    // separate household_confidence sidecar; the confidence-bearing path
    // is consolidated under demographics_confidence with section
    // discrimination decided by the issuer not the encoder.
    if (Object.keys(claims.household).length > 0) {
        for (const msg of encodeAttributeSection('household', claims.household, claims.demographics_confidence)) {
            messages.push(msg);
        }
    }
    if (claims.reputation_level) {
        messages.push(lengthPrefixedConcat([utf8(REP_LABEL), utf8(claims.reputation_level)]));
    }
    return messages;
}
function readChunks(encoded) {
    if (encoded.length === 0)
        return null;
    const chunks = [];
    let off = 0;
    const view = new DataView(encoded.buffer, encoded.byteOffset, encoded.byteLength);
    while (off < encoded.length) {
        if (off + 4 > encoded.length)
            return null;
        const len = view.getUint32(off, false);
        off += 4;
        if (off + len > encoded.length)
            return null;
        chunks.push(encoded.slice(off, off + len));
        off += len;
    }
    return chunks;
}
export function decodeDisclosedMessage(encoded) {
    const chunks = readChunks(encoded);
    if (chunks === null || chunks.length === 0) {
        return { kind: 'malformed', reason: 'empty_message' };
    }
    const decoder = new TextDecoder();
    const label = decoder.decode(chunks[0]);
    if (label === AUDIENCE_LABEL_V1) {
        if (chunks.length !== 5) {
            return {
                kind: 'malformed',
                reason: 'wrong_chunk_count',
                details: `audience:v1 expected 5 chunks, got ${chunks.length}`,
            };
        }
        const section = decoder.decode(chunks[1]);
        if (!AUDIENCE_SECTIONS.has(section)) {
            return { kind: 'malformed', reason: 'invalid_section', details: section };
        }
        const taxonomy = decoder.decode(chunks[2]);
        if (!TAXONOMY_FORMAT_GUARD.test(taxonomy)) {
            return { kind: 'malformed', reason: 'invalid_taxonomy', details: taxonomy };
        }
        const id = decoder.decode(chunks[3]);
        if (!AUDIENCE_ID_REGEX.test(id)) {
            return { kind: 'malformed', reason: 'invalid_id', details: id };
        }
        if (chunks[4].length !== 4) {
            return {
                kind: 'malformed',
                reason: 'invalid_confidence',
                details: `confidence chunk must be 4 bytes, got ${chunks[4].length}`,
            };
        }
        const confView = new DataView(chunks[4].buffer, chunks[4].byteOffset, chunks[4].byteLength);
        const confidence = confView.getFloat32(0, false);
        if (!Number.isFinite(confidence) || confidence < 0 || confidence > 1) {
            return {
                kind: 'malformed',
                reason: 'invalid_confidence',
                details: String(confidence),
            };
        }
        return {
            kind: 'audience_segment',
            version: 'v1',
            section: section,
            taxonomy,
            id,
            confidence,
        };
    }
    if (label === ATTRIBUTE_LABEL_V1) {
        // Reserved for AT-14. Decode protocol-locally; semantic discipline
        // (canonical_demographics value schemas) runs in the wallet validator
        // outside the decoder.
        if (chunks.length !== 5) {
            return {
                kind: 'malformed',
                reason: 'wrong_chunk_count',
                details: `attribute:v1 expected 5 chunks, got ${chunks.length}`,
            };
        }
        const section = decoder.decode(chunks[1]);
        if (!ATTRIBUTE_SECTIONS.has(section)) {
            return { kind: 'malformed', reason: 'invalid_section', details: section };
        }
        const key = decoder.decode(chunks[2]);
        const value = decoder.decode(chunks[3]);
        if (chunks[4].length !== 4) {
            return {
                kind: 'malformed',
                reason: 'invalid_confidence',
                details: `confidence chunk must be 4 bytes, got ${chunks[4].length}`,
            };
        }
        const confView = new DataView(chunks[4].buffer, chunks[4].byteOffset, chunks[4].byteLength);
        const confidence = confView.getFloat32(0, false);
        if (!Number.isFinite(confidence) || confidence < 0 || confidence > 1) {
            return {
                kind: 'malformed',
                reason: 'invalid_confidence',
                details: String(confidence),
            };
        }
        return {
            kind: 'user_attribute',
            version: 'v1',
            section: section,
            key,
            value,
            confidence,
        };
    }
    if (label === REP_LABEL) {
        if (chunks.length !== 2) {
            return {
                kind: 'malformed',
                reason: 'wrong_chunk_count',
                details: `rep expected 2 chunks, got ${chunks.length}`,
            };
        }
        const level = decoder.decode(chunks[1]);
        if (!REPUTATION_LEVELS.has(level)) {
            return { kind: 'malformed', reason: 'invalid_level', details: level };
        }
        return { kind: 'reputation', level: level };
    }
    // Pre-migration labels (`interest[i]`, `intent[i]`, `demo[key]`) and
    // any other unrecognised label. ADR 0005 Q8: `unknown` is a hard
    // verification failure at the dispatch layer — see verifier.ts.
    return { kind: 'unknown', label };
}
const ASSURANCE_HIERARCHY = [
    'issuer-backed',
    'authentication-artifact-derived',
    'platform-observed',
    'wallet-derived',
];
export function buildGrantAssuranceSummary(credentials) {
    let best = ASSURANCE_HIERARCHY.length - 1;
    for (const vc of credentials) {
        const ac = vc.evidence?.[0]?.assuranceClass ?? 'wallet-derived';
        const idx = ASSURANCE_HIERARCHY.indexOf(ac);
        if (idx !== -1 && idx < best)
            best = idx;
    }
    return { max_assurance_class: ASSURANCE_HIERARCHY[best] };
}
/**
 * Reconstruct BBS+ message bytes from disclosed claims at known indexes.
 * The verifier uses this to verify a selective disclosure proof: it needs
 * the exact message bytes that were signed, for each disclosed index.
 */
export function disclosedClaimsToMessages(full, disclosedIndexes) {
    const allMessages = claimsToMessages(full);
    return disclosedIndexes.map(idx => allMessages[idx]);
}
/**
 * Reconstruct BBS+ messages for disclosed claims without requiring the
 * full NormalizedClaimSet. Mirrors `claimsToMessages` exactly so the
 * audience-segment 5-tuple and user-attribute 5-tuple shapes are identical
 * on both sides — required for selective-disclosure proof verification.
 *
 * AT-14: demographic disclosure uses `attribute:v1` 5-tuple. The disclosure
 * engine carries `disclosed_demographics_confidence` forward; demographic
 * entries without confidence are dropped on the verifier side too (matching
 * the issuer-side encoder behaviour).
 */
export function rebuildDisclosedMessages(disclosed) {
    const messages = [];
    for (const claim of disclosed.disclosed_interests) {
        messages.push(encodeAudienceSegment('interests', claim));
    }
    for (const claim of disclosed.disclosed_purchase_intent) {
        messages.push(encodeAudienceSegment('purchase_intent', claim));
    }
    for (const msg of encodeAttributeSection('demographics', disclosed.disclosed_demographics, disclosed.disclosed_demographics_confidence)) {
        messages.push(msg);
    }
    if (disclosed.disclosed_reputation_level) {
        messages.push(lengthPrefixedConcat([utf8(REP_LABEL), utf8(disclosed.disclosed_reputation_level)]));
    }
    return messages;
}
